import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../../api/client';
import {
  addStudioGalleryImage,
  applyStudioPrimaryImage,
  clearStudioPrimaryImage,
  createStudio,
  getStudioDetail,
  removeStudioGalleryImage,
  updateStudioBasicInfo,
  updateStudioBusinessHours,
  updateStudioKeywords,
  type StudioBusinessHour,
  type StudioDetail,
} from '../../api/partnerStudios';
import { useToast } from '../../components/ui/ToastProvider';
import { uploadPartnerImage } from '../../lib/partnerUpload';
import { usePartnerContext } from './PartnerLayout';

const DAY_ORDER = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const;

const DAY_LABELS: Record<string, string> = {
  MONDAY: '월',
  TUESDAY: '화',
  WEDNESDAY: '수',
  THURSDAY: '목',
  FRIDAY: '금',
  SATURDAY: '토',
  SUNDAY: '일',
};

type HourRow = {
  dayOfWeek: string;
  closed: boolean;
  openTime: string;
  closeTime: string;
};

type KeywordRow = {
  primaryCategory: string;
  secondaryKeyword: string;
};

function defaultHours(): HourRow[] {
  return DAY_ORDER.map((day) => ({
    dayOfWeek: day,
    closed: false,
    openTime: '10:00',
    closeTime: '22:00',
  }));
}

function toHourRows(hours: StudioBusinessHour[]): HourRow[] {
  if (!hours || hours.length === 0) return defaultHours();
  const byDay = new Map(hours.map((row) => [row.dayOfWeek, row]));
  return DAY_ORDER.map((day) => {
    const row = byDay.get(day);
    return {
      dayOfWeek: day,
      closed: row?.closed ?? false,
      openTime: (row?.openTime ?? '10:00').slice(0, 5),
      closeTime: (row?.closeTime ?? '22:00').slice(0, 5),
    };
  });
}

function errText(error: unknown, fallback: string) {
  if (error instanceof ApiError && error.message) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function PartnerStudioEditPage() {
  const navigate = useNavigate();
  const { studioId } = useParams();
  const isNew = !studioId;
  const { reloadStudios } = usePartnerContext();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState('');
  const [studio, setStudio] = useState<StudioDetail | null>(null);

  // --- 기본 정보 폼 ---
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [roadAddress, setRoadAddress] = useState('');
  const [detailAddress, setDetailAddress] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [parkingType, setParkingType] = useState('NONE');
  const [parkingMethod, setParkingMethod] = useState('');
  const [parkingFeeDescription, setParkingFeeDescription] = useState('');
  const [homepageUrls, setHomepageUrls] = useState('');
  const [savingBasic, setSavingBasic] = useState(false);

  // --- 영업시간 / 키워드 ---
  const [hours, setHours] = useState<HourRow[]>(defaultHours());
  const [holidayNote, setHolidayNote] = useState('');
  const [savingHours, setSavingHours] = useState(false);
  const [keywords, setKeywords] = useState<KeywordRow[]>([]);
  const [savingKeywords, setSavingKeywords] = useState(false);

  // --- 이미지 ---
  const primaryInputRef = useRef<HTMLInputElement | null>(null);
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingPrimary, setUploadingPrimary] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  const applyStudioToForm = useCallback((data: StudioDetail) => {
    setStudio(data);
    setName(data.name ?? '');
    setSlug(data.slug ?? '');
    setDescription(data.description ?? '');
    setContactPhone(data.contactPhone ?? '');
    setRoadAddress(data.address?.roadAddress ?? '');
    setDetailAddress(data.address?.detailAddress ?? '');
    setLatitude(data.address?.latitude != null ? String(data.address.latitude) : '');
    setLongitude(data.address?.longitude != null ? String(data.address.longitude) : '');
    setParkingType(data.parkingPolicy?.parkingType ?? 'NONE');
    setParkingMethod(data.parkingPolicy?.parkingMethod ?? '');
    setParkingFeeDescription(data.parkingPolicy?.parkingFeeDescription ?? '');
    setHomepageUrls((data.homepageUrls ?? []).join('\n'));
    setHours(toHourRows(data.businessHours ?? []));
    setHolidayNote(data.holidayNote ?? '');
    setKeywords(
      (data.keywords ?? []).map((row) => ({
        primaryCategory: row.primaryCategory,
        secondaryKeyword: row.secondaryKeyword,
      })),
    );
  }, []);

  const reloadDetail = useCallback(async () => {
    if (!studioId) return;
    const data = await getStudioDetail(studioId);
    applyStudioToForm(data);
  }, [applyStudioToForm, studioId]);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    reloadDetail()
      .catch((error) => {
        if (!cancelled) setLoadError(errText(error, '스튜디오 정보를 불러오지 못했습니다.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isNew, reloadDetail]);

  const buildBasicRequest = () => ({
    slug: slug.trim() || undefined,
    name: name.trim(),
    description: description.trim() || undefined,
    contactPhone: contactPhone.trim() || undefined,
    address: roadAddress.trim()
      ? {
          roadAddress: roadAddress.trim(),
          detailAddress: detailAddress.trim() || null,
          latitude: latitude.trim() ? Number(latitude) : null,
          longitude: longitude.trim() ? Number(longitude) : null,
        }
      : undefined,
    parkingPolicy: {
      parkingType,
      parkingMethod: parkingMethod.trim() || null,
      parkingFeeDescription: parkingFeeDescription.trim() || null,
    },
    homepageUrls: homepageUrls
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean),
  });

  const submitBasic = async () => {
    if (!name.trim()) {
      showToast('스튜디오 이름을 입력하세요.');
      return;
    }
    setSavingBasic(true);
    try {
      if (isNew) {
        const created = await createStudio(buildBasicRequest());
        showToast('스튜디오를 등록했습니다.');
        await reloadStudios();
        navigate(`/partner/studios/${created.studioId}`, { replace: true });
      } else {
        await updateStudioBasicInfo(studioId!, buildBasicRequest());
        showToast('기본 정보를 저장했습니다.');
        await Promise.all([reloadDetail(), reloadStudios()]);
      }
    } catch (error) {
      showToast(errText(error, '저장에 실패했습니다.'));
    } finally {
      setSavingBasic(false);
    }
  };

  const submitHours = async () => {
    if (!studioId) return;
    setSavingHours(true);
    try {
      await updateStudioBusinessHours(studioId, {
        businessHours: hours.map((row) => ({
          dayOfWeek: row.dayOfWeek,
          closed: row.closed,
          openTime: row.closed ? null : row.openTime,
          closeTime: row.closed ? null : row.closeTime,
        })),
        holidayNote: holidayNote.trim() || undefined,
      });
      showToast('영업시간을 저장했습니다.');
    } catch (error) {
      showToast(errText(error, '영업시간 저장에 실패했습니다.'));
    } finally {
      setSavingHours(false);
    }
  };

  const submitKeywords = async () => {
    if (!studioId) return;
    const cleaned = keywords
      .map((row) => ({
        primaryCategory: row.primaryCategory.trim(),
        secondaryKeyword: row.secondaryKeyword.trim(),
      }))
      .filter((row) => row.primaryCategory && row.secondaryKeyword);
    setSavingKeywords(true);
    try {
      await updateStudioKeywords(studioId, cleaned);
      showToast('키워드를 저장했습니다.');
    } catch (error) {
      showToast(errText(error, '키워드 저장에 실패했습니다.'));
    } finally {
      setSavingKeywords(false);
    }
  };

  const onPrimarySelected = async (file: File | null) => {
    if (!file || !studioId) return;
    setUploadingPrimary(true);
    try {
      const uploaded = await uploadPartnerImage(file, {
        targetType: 'STUDIO_PRIMARY_IMAGE',
        studioId,
      });
      await applyStudioPrimaryImage(studioId, uploaded);
      showToast('대표 이미지를 적용했습니다.');
      await reloadDetail();
    } catch (error) {
      showToast(errText(error, '대표 이미지 업로드에 실패했습니다.'));
    } finally {
      setUploadingPrimary(false);
      if (primaryInputRef.current) primaryInputRef.current.value = '';
    }
  };

  const onGallerySelected = async (file: File | null) => {
    if (!file || !studioId) return;
    setUploadingGallery(true);
    try {
      const uploaded = await uploadPartnerImage(file, {
        targetType: 'STUDIO_GALLERY_IMAGE',
        studioId,
      });
      await addStudioGalleryImage(studioId, uploaded);
      showToast('갤러리 이미지를 추가했습니다.');
      await reloadDetail();
    } catch (error) {
      showToast(errText(error, '갤러리 이미지 업로드에 실패했습니다.'));
    } finally {
      setUploadingGallery(false);
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  const removeGalleryImage = async (galleryImageId: string) => {
    if (!studioId) return;
    if (!window.confirm('이 이미지를 삭제할까요?')) return;
    try {
      await removeStudioGalleryImage(studioId, galleryImageId);
      showToast('이미지를 삭제했습니다.');
      await reloadDetail();
    } catch (error) {
      showToast(errText(error, '이미지 삭제에 실패했습니다.'));
    }
  };

  const removePrimaryImage = async () => {
    if (!studioId) return;
    try {
      await clearStudioPrimaryImage(studioId);
      showToast('대표 이미지를 해제했습니다.');
      await reloadDetail();
    } catch (error) {
      showToast(errText(error, '대표 이미지 해제에 실패했습니다.'));
    }
  };

  if (loading) {
    return <p className="partner-empty">불러오는 중…</p>;
  }
  if (loadError) {
    return <div className="partner-error">{loadError}</div>;
  }

  return (
    <>
      <h1 className="partner-title">{isNew ? '스튜디오 등록' : '스튜디오 수정'}</h1>
      <p className="partner-subtitle">
        {isNew
          ? '기본 정보를 먼저 등록하면 영업시간·키워드·이미지를 이어서 설정할 수 있습니다.'
          : `${studio?.name ?? ''} 정보를 관리합니다.`}
      </p>

      <div className="partner-card">
        <h2 className="partner-card__title">기본 정보</h2>
        <div className="partner-form">
          <div className="partner-form__row">
            <label className="partner-field">
              <span className="partner-field__label">이름 *</span>
              <input
                onChange={(event) => setName(event.target.value)}
                type="text"
                value={name}
              />
            </label>
            <label className="partner-field">
              <span className="partner-field__label">
                슬러그 <small>(URL 식별자)</small>
              </span>
              <input
                onChange={(event) => setSlug(event.target.value)}
                placeholder="my-studio"
                type="text"
                value={slug}
              />
            </label>
            <label className="partner-field">
              <span className="partner-field__label">연락처</span>
              <input
                onChange={(event) => setContactPhone(event.target.value)}
                placeholder="02-0000-0000"
                type="tel"
                value={contactPhone}
              />
            </label>
          </div>
          <label className="partner-field">
            <span className="partner-field__label">소개</span>
            <textarea
              onChange={(event) => setDescription(event.target.value)}
              value={description}
            />
          </label>
          <div className="partner-form__row">
            <label className="partner-field">
              <span className="partner-field__label">도로명 주소</span>
              <input
                onChange={(event) => setRoadAddress(event.target.value)}
                type="text"
                value={roadAddress}
              />
            </label>
            <label className="partner-field">
              <span className="partner-field__label">상세 주소</span>
              <input
                onChange={(event) => setDetailAddress(event.target.value)}
                type="text"
                value={detailAddress}
              />
            </label>
          </div>
          <div className="partner-form__row">
            <label className="partner-field">
              <span className="partner-field__label">위도</span>
              <input
                onChange={(event) => setLatitude(event.target.value)}
                placeholder="37.5665"
                type="text"
                value={latitude}
              />
            </label>
            <label className="partner-field">
              <span className="partner-field__label">경도</span>
              <input
                onChange={(event) => setLongitude(event.target.value)}
                placeholder="126.9780"
                type="text"
                value={longitude}
              />
            </label>
            <label className="partner-field">
              <span className="partner-field__label">주차</span>
              <select onChange={(event) => setParkingType(event.target.value)} value={parkingType}>
                <option value="NONE">불가</option>
                <option value="FREE">무료</option>
                <option value="PAID">유료</option>
              </select>
            </label>
          </div>
          <div className="partner-form__row">
            <label className="partner-field">
              <span className="partner-field__label">주차 방법</span>
              <input
                onChange={(event) => setParkingMethod(event.target.value)}
                placeholder="건물 내 기계식"
                type="text"
                value={parkingMethod}
              />
            </label>
            <label className="partner-field">
              <span className="partner-field__label">주차 요금 안내</span>
              <input
                onChange={(event) => setParkingFeeDescription(event.target.value)}
                placeholder="30분 무료, 이후 10분당 500원"
                type="text"
                value={parkingFeeDescription}
              />
            </label>
          </div>
          <label className="partner-field">
            <span className="partner-field__label">
              홈페이지/SNS <small>(줄바꿈으로 여러 개)</small>
            </span>
            <textarea
              onChange={(event) => setHomepageUrls(event.target.value)}
              placeholder={'https://instagram.com/...\nhttps://...'}
              value={homepageUrls}
            />
          </label>
          <div className="partner-form__actions">
            <button
              className="partner-btn"
              disabled={savingBasic}
              onClick={submitBasic}
              type="button"
            >
              {savingBasic ? '저장 중…' : isNew ? '스튜디오 등록' : '기본 정보 저장'}
            </button>
          </div>
        </div>
      </div>

      {!isNew ? (
        <>
          <div className="partner-card">
            <h2 className="partner-card__title">영업시간</h2>
            <div className="partner-hours-grid">
              {hours.map((row, index) => (
                <div className="partner-hours-row" key={row.dayOfWeek}>
                  <span className="partner-hours-row__day">{DAY_LABELS[row.dayOfWeek]}</span>
                  <label className="partner-field partner-field--inline">
                    <input
                      checked={row.closed}
                      onChange={(event) =>
                        setHours((prev) =>
                          prev.map((item, i) =>
                            i === index ? { ...item, closed: event.target.checked } : item,
                          ),
                        )
                      }
                      type="checkbox"
                    />
                    <span>휴무</span>
                  </label>
                  <input
                    aria-label={`${DAY_LABELS[row.dayOfWeek]} 오픈 시간`}
                    className="partner-input"
                    disabled={row.closed}
                    onChange={(event) =>
                      setHours((prev) =>
                        prev.map((item, i) =>
                          i === index ? { ...item, openTime: event.target.value } : item,
                        ),
                      )
                    }
                    type="time"
                    value={row.openTime}
                  />
                  <span>~</span>
                  <input
                    aria-label={`${DAY_LABELS[row.dayOfWeek]} 마감 시간`}
                    className="partner-input"
                    disabled={row.closed}
                    onChange={(event) =>
                      setHours((prev) =>
                        prev.map((item, i) =>
                          i === index ? { ...item, closeTime: event.target.value } : item,
                        ),
                      )
                    }
                    type="time"
                    value={row.closeTime}
                  />
                </div>
              ))}
            </div>
            <label className="partner-field" style={{ marginTop: 14 }}>
              <span className="partner-field__label">휴무 안내 (최대 300자)</span>
              <input
                maxLength={300}
                onChange={(event) => setHolidayNote(event.target.value)}
                placeholder="명절 당일 휴무"
                type="text"
                value={holidayNote}
              />
            </label>
            <div className="partner-form__actions">
              <button
                className="partner-btn"
                disabled={savingHours}
                onClick={submitHours}
                type="button"
              >
                {savingHours ? '저장 중…' : '영업시간 저장'}
              </button>
            </div>
          </div>

          <div className="partner-card">
            <h2 className="partner-card__title">키워드 (최대 10개)</h2>
            <p className="partner-card__hint">
              대분류/세부 키워드는 검색 필터와 공간 상세 태그에 노출됩니다.
            </p>
            <div className="partner-rows">
              {keywords.map((row, index) => (
                <div className="partner-row-item" key={index}>
                  <input
                    aria-label="대분류"
                    onChange={(event) =>
                      setKeywords((prev) =>
                        prev.map((item, i) =>
                          i === index ? { ...item, primaryCategory: event.target.value } : item,
                        ),
                      )
                    }
                    placeholder="대분류 (예: 합주실)"
                    type="text"
                    value={row.primaryCategory}
                  />
                  <input
                    aria-label="세부 키워드"
                    onChange={(event) =>
                      setKeywords((prev) =>
                        prev.map((item, i) =>
                          i === index ? { ...item, secondaryKeyword: event.target.value } : item,
                        ),
                      )
                    }
                    placeholder="세부 키워드 (예: 드럼)"
                    type="text"
                    value={row.secondaryKeyword}
                  />
                  <button
                    className="partner-btn partner-btn--danger partner-btn--sm"
                    onClick={() =>
                      setKeywords((prev) => prev.filter((_, i) => i !== index))
                    }
                    type="button"
                  >
                    삭제
                  </button>
                </div>
              ))}
            </div>
            <div className="partner-form__actions" style={{ marginTop: 12 }}>
              <button
                className="partner-btn partner-btn--ghost partner-btn--sm"
                disabled={keywords.length >= 10}
                onClick={() =>
                  setKeywords((prev) => [...prev, { primaryCategory: '', secondaryKeyword: '' }])
                }
                type="button"
              >
                + 키워드 추가
              </button>
              <button
                className="partner-btn"
                disabled={savingKeywords}
                onClick={submitKeywords}
                type="button"
              >
                {savingKeywords ? '저장 중…' : '키워드 저장'}
              </button>
            </div>
          </div>

          <div className="partner-card">
            <h2 className="partner-card__title">대표 이미지</h2>
            {studio?.primaryImageUrl ? (
              <div className="partner-gallery" style={{ marginBottom: 12 }}>
                <div className="partner-gallery__item">
                  <img alt="대표 이미지" src={studio.primaryImageUrl} />
                  <span className="partner-gallery__badge">대표</span>
                </div>
              </div>
            ) : (
              <p className="partner-card__hint">대표 이미지가 없습니다.</p>
            )}
            <div className="partner-form__actions" style={{ justifyContent: 'flex-start' }}>
              <input
                accept="image/*"
                hidden
                onChange={(event) => onPrimarySelected(event.target.files?.[0] ?? null)}
                ref={primaryInputRef}
                type="file"
              />
              <button
                className="partner-btn partner-btn--ghost partner-btn--sm"
                disabled={uploadingPrimary}
                onClick={() => primaryInputRef.current?.click()}
                type="button"
              >
                {uploadingPrimary ? '업로드 중…' : '대표 이미지 업로드'}
              </button>
              {studio?.primaryImageUrl ? (
                <button
                  className="partner-btn partner-btn--danger partner-btn--sm"
                  onClick={removePrimaryImage}
                  type="button"
                >
                  대표 이미지 해제
                </button>
              ) : null}
            </div>
          </div>

          <div className="partner-card">
            <h2 className="partner-card__title">갤러리</h2>
            {studio && studio.gallery.length > 0 ? (
              <div className="partner-gallery" style={{ marginBottom: 12 }}>
                {studio.gallery
                  .slice()
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((image) => (
                    <div className="partner-gallery__item" key={image.galleryImageId}>
                      {image.imageUrl ? <img alt="" src={image.imageUrl} /> : null}
                      <button
                        aria-label="이미지 삭제"
                        className="partner-gallery__remove"
                        onClick={() => removeGalleryImage(String(image.galleryImageId))}
                        type="button"
                      >
                        ×
                      </button>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="partner-card__hint">갤러리 이미지가 없습니다.</p>
            )}
            <input
              accept="image/*"
              hidden
              onChange={(event) => onGallerySelected(event.target.files?.[0] ?? null)}
              ref={galleryInputRef}
              type="file"
            />
            <button
              className="partner-btn partner-btn--ghost partner-btn--sm"
              disabled={uploadingGallery}
              onClick={() => galleryInputRef.current?.click()}
              type="button"
            >
              {uploadingGallery ? '업로드 중…' : '+ 갤러리 이미지 추가'}
            </button>
          </div>
        </>
      ) : null}
    </>
  );
}
